import { Controller } from "@hotwired/stimulus"

// Drives the admin loop-preview page: detects HTML5 video loop boundaries
// (currentTime wrapping past zero) and updates the on-screen HUD so the
// viewer can verify the loop fires cleanly. Purely client-side observation
// of the <video loop> element.
//
// HUD outputs a large time display, a progress bar, time-until-wrap, and a
// yellow flash at each loop boundary (default on, toggle with F).
export default class extends Controller {
  static targets = ["video", "count", "time", "duration", "timeToLoop", "progress", "progressBar", "flash", "flashStatus", "rateButton"]

  connect() {
    this.loops = 0
    this.lastTime = 0
    this.flashEnabled = true
    this.currentRate = 1.0

    const video = this.videoTarget
    video.addEventListener("loadedmetadata", this.onMeta)
    video.addEventListener("timeupdate", this.onTime)
    video.addEventListener("seeked", this.onSeek)

    document.addEventListener("keydown", this.onKey)

    this.highlightRateButton(1.0)
    this.updateFlashStatus()
  }

  disconnect() {
    document.removeEventListener("keydown", this.onKey)
  }

  onMeta = () => {
    if (this.hasDurationTarget) {
      this.durationTarget.textContent = this.videoTarget.duration.toFixed(2)
    }
  }

  onTime = () => {
    const video = this.videoTarget
    const t = video.currentTime
    const d = video.duration || 0

    if (this.hasTimeTarget) {
      this.timeTarget.textContent = t.toFixed(2)
    }
    if (this.hasTimeToLoopTarget) {
      const remaining = Math.max(0, d - t)
      this.timeToLoopTarget.textContent = remaining.toFixed(2)
    }
    if (this.hasProgressTarget && d > 0) {
      this.progressTarget.style.width = `${(t / d) * 100}%`
    }

    // Loop-wrap detection: currentTime resets from high to low.
    if (t + 0.5 < this.lastTime) {
      this.registerLoop()
    }
    this.lastTime = t
  }

  onSeek = () => {
    // seeked fires when the browser rewinds for the HTML5 loop. Only
    // count it when we came from near-end and landed near-zero to avoid
    // double-counting manual seeks via the progress bar.
    if (this.videoTarget.currentTime < 0.2 && this.lastTime > 0.5 && !this.manualSeek) {
      this.registerLoop()
    }
    this.manualSeek = false
  }

  // Advance exactly one Veo-source frame (1/24s). Auto-pauses; stepping
  // only makes sense frame-at-a-time. Wraps at the file boundary so you
  // can inspect the loop seam at t=end → t=0 one frame at a time.
  stepForward() {
    this.stepFrame(1)
  }

  stepBack() {
    this.stepFrame(-1)
  }

  stepFrame(direction) {
    const video = this.videoTarget
    if (!video.paused) video.pause()
    const step = 1 / 24
    const d = video.duration || 0
    let t = video.currentTime + direction * step
    // Wrap around so stepping forward past end lands at the start (the
    // same hard-loop behavior we're studying), and stepping back before
    // start lands near the end.
    if (t >= d) t = t - d
    if (t < 0)  t = d + t
    this.manualSeek = true
    video.currentTime = t
    this.lastTime = t
  }

  // Click on the progress bar to seek to that position.
  seek(event) {
    const bar = this.progressBarTarget
    const rect = bar.getBoundingClientRect()
    const pct = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width))
    const video = this.videoTarget
    if (video.duration && !Number.isNaN(video.duration)) {
      this.manualSeek = true
      video.currentTime = pct * video.duration
      this.lastTime = video.currentTime
      // If paused, playback rate stays; if playing, it just continues.
    }
  }

  registerLoop() {
    this.loops += 1
    if (this.hasCountTarget) {
      this.countTarget.textContent = this.loops
    }
    if (this.flashEnabled && this.hasFlashTarget) {
      const el = this.flashTarget
      el.style.boxShadow = "inset 0 0 0 12px #ffdc00"
      setTimeout(() => { el.style.boxShadow = "inset 0 0 0 0 #ffdc00" }, 180)
    }
  }

  setRate(event) {
    const rate = parseFloat(event.currentTarget.dataset.loopPreviewRateParam)
    if (!Number.isNaN(rate)) {
      this.videoTarget.playbackRate = rate
      this.currentRate = rate
      this.highlightRateButton(rate)
    }
  }

  highlightRateButton(rate) {
    if (!this.hasRateButtonTarget) return
    this.rateButtonTargets.forEach((btn) => {
      const btnRate = parseFloat(btn.dataset.rate)
      if (btnRate === rate) {
        btn.style.background = "#ffdc00"
        btn.style.color = "#000"
      } else {
        btn.style.background = "rgba(255,255,255,0.1)"
        btn.style.color = "#fff"
      }
    })
  }

  updateFlashStatus() {
    if (this.hasFlashStatusTarget) {
      this.flashStatusTarget.textContent = this.flashEnabled ? "ON" : "OFF"
      this.flashStatusTarget.style.color = this.flashEnabled ? "#ffdc00" : "rgba(255,255,255,0.5)"
    }
  }

  onKey = (event) => {
    if (event.key === "f" || event.key === "F") {
      this.flashEnabled = !this.flashEnabled
      this.updateFlashStatus()
      event.preventDefault()
    } else if (event.key === "r" || event.key === "R") {
      this.videoTarget.currentTime = 0
      this.lastTime = 0
      event.preventDefault()
    } else if (event.key === " ") {
      if (this.videoTarget.paused) {
        this.videoTarget.play()
      } else {
        this.videoTarget.pause()
      }
      event.preventDefault()
    } else if (event.key === "ArrowRight") {
      this.stepForward()
      event.preventDefault()
    } else if (event.key === "ArrowLeft") {
      this.stepBack()
      event.preventDefault()
    }
  }
}
